import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Gift, Copy, CheckCircle, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralPage() {
  const { user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [applyMsg, setApplyMsg] = useState('');
  const [applyErr, setApplyErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/referral`, { withCredentials: true });
        setReferralData(res.data);
      } catch {}
    })();
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setApplyMsg('');
    setApplyErr('');
    try {
      const res = await axios.post(`${API}/referral/apply`, { code: applyCode }, { withCredentials: true });
      setApplyMsg(res.data.message);
      setApplyCode('');
    } catch (err) {
      setApplyErr(err.response?.data?.detail || 'Error al aplicar código');
    }
  };

  return (
    <div className="p-6 md:p-10 pb-24 md:pb-10 max-w-5xl" data-testid="referral-page">
      <div className="mb-10 animate-fade-in-up">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight uppercase text-white">Referidos</h1>
        <p className="text-zinc-500 mt-2">Trae amigos y gana un 20% de su tatuaje</p>
      </div>

      {/* Your code */}
      <div className="bg-diavel-card border border-white/5 p-8 mb-8 animate-fade-in-up animate-delay-100">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-red-900/20 flex items-center justify-center">
            <Gift className="w-6 h-6 text-diavel-red" />
          </div>
          <div>
            <p className="font-mono text-xs tracking-wider uppercase text-zinc-500">Tu código de referido</p>
            <p className="font-heading font-bold text-2xl text-white tracking-wider">{user?.referral_code}</p>
          </div>
        </div>
        <button
          onClick={handleCopy} data-testid="copy-referral-btn"
          className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-8 hover:bg-red-700 transition-all duration-300 hover:shadow-[0_0_20px_rgba(196,30,30,0.4)] flex items-center gap-2 text-sm"
        >
          {copied ? <><CheckCircle className="w-4 h-4" /> Copiado</> : <><Copy className="w-4 h-4" /> Copiar código</>}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-8 animate-fade-in-up animate-delay-200">
        <div className="bg-diavel-card border border-white/5 p-6">
          <Users className="w-5 h-5 text-zinc-600 mb-3" />
          <p className="font-heading font-bold text-2xl text-white">{referralData?.total_referrals || 0}</p>
          <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mt-1">Referidos</p>
        </div>
        <div className="bg-diavel-card border border-white/5 p-6">
          <Gift className="w-5 h-5 text-zinc-600 mb-3" />
          <p className="font-heading font-bold text-2xl text-green-400">{(referralData?.total_earned || 0).toFixed(2)}€</p>
          <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mt-1">Ganado</p>
        </div>
      </div>

      {/* Apply code */}
      <div className="bg-diavel-card border border-white/5 p-6 mb-8 animate-fade-in-up animate-delay-300">
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-white mb-4">¿Tienes un código de referido?</h3>
        <form onSubmit={handleApply} className="flex gap-3">
          <input
            data-testid="apply-referral-input"
            type="text" value={applyCode} onChange={e => setApplyCode(e.target.value.toUpperCase())}
            className="flex-1 bg-zinc-900/50 border border-white/10 focus:border-red-600 focus:ring-1 focus:ring-red-600/50 h-12 px-4 text-white placeholder:text-zinc-600 outline-none transition-all duration-300 font-mono tracking-wider"
            placeholder="CÓDIGO" required
          />
          <button data-testid="apply-referral-btn" type="submit"
            className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-6 hover:bg-red-700 transition-all duration-300 text-sm">
            Aplicar
          </button>
        </form>
        {applyMsg && <p className="text-green-400 text-sm mt-3">{applyMsg}</p>}
        {applyErr && <p className="text-red-400 text-sm mt-3">{applyErr}</p>}
      </div>

      {/* Referral list */}
      {referralData?.referrals?.length > 0 && (
        <div className="bg-diavel-card border border-white/5 p-6 animate-fade-in-up animate-delay-400">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-white mb-4">Tus referidos</h3>
          <div className="space-y-3">
            {referralData.referrals.map((ref, i) => (
              <div key={i} className="flex justify-between items-center py-3 border-b border-white/5 last:border-0">
                <div>
                  <p className="text-sm text-white">{ref.referred_name || 'Anónimo'}</p>
                  <p className="font-mono text-xs text-zinc-600">{new Date(ref.created_at).toLocaleDateString('es-ES')}</p>
                </div>
                <span className="font-heading font-bold text-sm text-green-400">+{(ref.earned || 0).toFixed(2)}€</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
